/**
 * Luật bật tắt từng thao tác trên một dòng nhân sự nền tảng (ADR-0057 mục 5).
 *
 * Hàm thuần, **view và máy chủ giả dùng chung** để hai bên không lệch luật.
 * Server vẫn là chốt chặn thật; ẩn mục ở giao diện chỉ để không bày ra cánh cửa
 * không mở được (ADR-UI-0005).
 */
import type { PlatformStaff, PlatformStaffRole, PlatformStaffStatus } from '../types';

/** Chỉ duyệt được tài khoản còn chờ, và người duyệt phải khác người đề nghị. */
export function duyetDuoc(s: PlatformStaff, toiLa: string | undefined): boolean {
  return s.trang_thai === 'CHO_DUYET' && s.nguoi_de_nghi !== toiLa;
}

/**
 * Vì sao nút duyệt bị ẩn — để giao diện nói đúng lý do thay vì im lặng.
 *
 * `tu-de-nghi` là ca dễ gây hoang mang nhất: người vừa đề nghị mở màn lên,
 * thấy tài khoản mình tạo mà không có nút nào.
 */
export type LoiDuyet = 'da-duyet' | 'tu-de-nghi' | null;

export function viSaoKhongDuyetDuoc(s: PlatformStaff, toiLa: string | undefined): LoiDuyet {
  if (s.trang_thai !== 'CHO_DUYET') return 'da-duyet';
  if (s.nguoi_de_nghi === toiLa) return 'tu-de-nghi';
  return null;
}

/**
 * Gửi lại email đặt mật khẩu được khi nào (ADR-0060 mục 3).
 *
 * Chỉ với tài khoản **đã duyệt** mà người đó **chưa cài xác thực hai lớp** — dấu hiệu còn gần nhất cho
 * "chưa đăng nhập lần nào", vì ADR-0007 mục 9 bắt buộc MFA nên ai đã dùng tài khoản đều đã cài. Còn
 * `CHO_DUYET` thì chưa có gì để bàn giao; đã cài rồi thì đổi mật khẩu là việc của chính họ.
 */
export function guiLaiDuoc(s: PlatformStaff): boolean {
  return s.trang_thai !== 'CHO_DUYET' && !s.mfa_configured;
}

/** Khoá được khi đang bật và không phải chính mình (ADR-0057 mục 5). */
export function khoaDuoc(s: PlatformStaff, toiLa: string | undefined): boolean {
  return s.trang_thai === 'ACTIVE' && s.id !== toiLa;
}

/** Mở khoá được khi đang bị khoá. Không cần loại trừ chính mình: người bị khoá không gọi được API. */
export function moKhoaDuoc(s: PlatformStaff): boolean {
  return s.trang_thai === 'DISABLED';
}

/** Đổi vai trò được khi đã duyệt và không phải chính mình. */
export function doiVaiTroDuoc(s: PlatformStaff, toiLa: string | undefined): boolean {
  return s.trang_thai !== 'CHO_DUYET' && s.id !== toiLa;
}

/**
 * Gán **vai trò nền tảng** (ADR-0074) được khi nào.
 *
 * Khác `doiVaiTroDuoc` ở trên: hàm đó nói về vai trò **lớp 1** ở Keycloak
 * (`PLATFORM_ADMIN`…), còn đây là vai trò lưu ở Control Plane, cộng dồn lên
 * trên vai trò lớp 1 (ADR-0074 mục 5).
 *
 * Ba điều kiện: tài khoản còn `CHO_DUYET` thì chưa có vai trò lớp 1 nên chưa
 * vào được cổng, gán thêm quyền là vô nghĩa; `PLATFORM_ADMIN` đã toàn quyền ở
 * cổng nền tảng, vai trò không cộng thêm hay thu hẹp được gì (ADR-0077 mục 5);
 * và không tự gán cho mình vì backend trả `403 SELF_ROLE_CHANGE_FORBIDDEN`
 * (ADR-0004) — ngoại lệ chỉ-gỡ-bớt không làm ở giao diện.
 */
export function ganVaiTroNenTangDuoc(s: PlatformStaff, toiLa: string | undefined): boolean {
  return s.trang_thai !== 'CHO_DUYET' && s.vai_tro !== 'PLATFORM_ADMIN' && s.id !== toiLa;
}

/** Đang bị realm khoá tạm vì nhập sai mật khẩu nhiều lần — khác với bị quản trị khoá. */
export function dangTamKhoaDangNhap(s: PlatformStaff): boolean {
  return s.login_locked === true && s.trang_thai !== 'DISABLED';
}

/**
 * Bị buộc MFA mà chưa cài: ADR-0007 mục 9 bắt buộc MFA cho cả ba vai trò, nên
 * mọi tài khoản đã duyệt mà `mfa_configured` false đều là chỗ cần để mắt.
 */
export function thieuMfa(s: PlatformStaff): boolean {
  return s.trang_thai === 'ACTIVE' && s.mfa_configured === false;
}

/** Vai trò bắt buộc email thuộc domain dành riêng của nền tảng (ADR-0007 mục 5). */
export function doiDomainDanhRieng(vaiTro: PlatformStaffRole): boolean {
  return vaiTro !== 'VENDOR';
}

/**
 * Sẽ mất `PLATFORM_ADMIN` cuối cùng đang hoạt động?
 *
 * Luật quan trọng nhất của ADR-0057 mục 5: khoá hay hạ vai trò người cuối cùng
 * thì không ai mở lại được bằng giao diện, mà script bootstrap cũng không cứu
 * được vì nó chỉ chạy khi realm **chưa** có ai mang `PLATFORM_ADMIN`.
 */
export function laAdminCuoiCung(s: PlatformStaff, tatCa: readonly PlatformStaff[]): boolean {
  if (s.vai_tro !== 'PLATFORM_ADMIN' || s.trang_thai !== 'ACTIVE') return false;
  return !tatCa.some(
    (x) => x.id !== s.id && x.vai_tro === 'PLATFORM_ADMIN' && x.trang_thai === 'ACTIVE',
  );
}

/** Mã lỗi backend của hai bước tạo và duyệt, để giao diện chọn đúng câu. */
export type LoiNhanSu =
  | 'email-da-co'
  | 'domain-cua-tenant'
  | 'domain-khong-danh-rieng'
  | 'admin-cuoi-cung'
  | 'tu-duyet'
  | 'khac';

export function phanLoaiLoi(code: string | undefined, status: number | undefined): LoiNhanSu {
  if (code === 'USER_ALREADY_EXISTS') return 'email-da-co';
  if (code === 'TENANT_DOMAIN_CONFLICT') return 'domain-cua-tenant';
  if (code === 'EMAIL_NOT_INVITABLE') return 'domain-khong-danh-rieng';
  if (code === 'TENANT_STATUS_CONFLICT') return 'admin-cuoi-cung';
  if (code === 'PERMISSION_DENIED' && status === 403) return 'tu-duyet';
  return 'khac';
}

/** Trạng thái hiện ở ô bộ lọc, theo thứ tự vòng đời. */
export const THU_TU_TRANG_THAI: readonly PlatformStaffStatus[] = ['CHO_DUYET', 'ACTIVE', 'DISABLED'];
